import React from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormHelperText from "@mui/material/FormHelperText";
import InstitutionsDropdown from "../../components/InstitutionsDropdown";
import {
  ConditionalRenderer,
  DatePicker,
  RolesDropdown,
  useLanguageTranslator,
} from "../../components";
import {
  MdCheck,
  MdDeleteForever,
  MdExpandMore,
  MdOutlineRemove,
} from "react-icons/md";
import { Controller } from "react-hook-form";
import CareerDetailTabs from "./CareerDetailTabs";
import CareerActionsForm from "./CareerActionsForm";
import CareerPositionsForm from "./CareerPositionsForm";

import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";

export type CareerFormProps = {
  name: string;
  disabled?: boolean;
};

const CareerForm: React.FC<CareerFormProps> = (props) => {
  const { name, disabled } = props;

  const location = useLocation();
  const { translator } = useLanguageTranslator();
  const isEdit = location?.pathname?.includes("edit");

  const {
    formState: { errors },
    getValues,
    watch,
  } = useFormContext();

  const { fields, append, update, remove } = useFieldArray({
    name,
  });

  const handleAddCareer = useCallback(() => {
    append({
      institutionId: "",
      roleId: "",
      startDate: "",
      endDate: "",
    });
  }, [append]);

  const handleRemoveAction = useCallback(
    (index: number) => () => {
      const fieldName = `${name}.${index}`;
      const fieldData = getValues(fieldName);

      // If career has an `id`. toggle `_destroy` key.
      if (fieldData.hasOwnProperty("id")) {
        update(index, { ...fieldData, _destroy: !fieldData._destroy });
      }
      // Else remove.
      else remove(index);
    },
    [fields, getValues, name, update, remove]
  );

  return (
    <>
      <Paper component={Container} sx={{ py: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4">{translator("Career")}</Typography>
          <Button
            variant="contained"
            disabled={disabled}
            onClick={handleAddCareer}
            sx={{ px: 6 }}
          >
            {translator("add new career")}
          </Button>
        </Stack>
      </Paper>

      {fields.map((data: any, i) => (
        <Accordion key={data.id} defaultExpanded>
          <AccordionSummary
            expandIcon={<MdExpandMore fontSize={28} />}
          ></AccordionSummary>
          <AccordionDetails sx={{ overflow: "hidden" }}>
            <Stack direction={{ md: "row" }} sx={{ mb: 3 }} spacing={3}>
              <Stack
                direction={{ sm: "row" }}
                sx={{ width: "0%" }}
                spacing={2}
                flex={1}
              >
                <Controller
                  name={`${name}.${i}.institutionId`}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl
                      required
                      disabled={disabled || data._destroy}
                      error={!!error}
                      sx={{ flex: 1 }}
                    >
                      <FormLabel>{translator("Institution")}</FormLabel>
                      <InstitutionsDropdown {...field} />
                      <FormHelperText>{error?.message}</FormHelperText>
                    </FormControl>
                  )}
                />

                <Controller
                  name={`${name}.${i}.roleId`}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl
                      required
                      disabled={disabled || data._destroy}
                      error={Boolean(error)}
                      sx={{ flex: 1 }}
                    >
                      <FormLabel>{translator("Role")}</FormLabel>
                      <RolesDropdown {...field} />
                      <FormHelperText>{error?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Stack>
              <Stack direction={{ sm: "row" }} spacing={2} flex={1}>
                <Controller
                  name={`${name}.${i}.startDate`}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormControl
                        required
                        disabled={disabled || data._destroy}
                        error={!!error}
                        sx={{ flex: 1, position: "relative" }}
                      >
                        <FormLabel>{translator("Period")}</FormLabel>
                        <DatePicker
                          selected={field?.value}
                          disabled={disabled || data._destroy}
                          name="startDate"
                          onSelect={(date: any) => {
                            if (date) {
                              const formattedDate =
                                dayjs(date).format("YYYY-MM-DD");
                              field.onChange(formattedDate);
                            }
                          }}
                        />
                        <FormHelperText>{error?.message}</FormHelperText>
                      </FormControl>
                    );
                  }}
                />
                <Stack
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexDirection: "column",
                  }}
                  spacing={2}
                  flex={1}
                >
                  <Controller
                    name={`${name}.${i}.present`}
                    defaultValue={false}
                    render={({ field: presentField }) => (
                      <FormControl
                        sx={{
                          flexDirection: "row",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={presentField.value}
                          onChange={(e) =>
                            presentField.onChange(e.target.checked)
                          }
                          id={`present-${i}`}
                        />
                        <label
                          htmlFor={`present-${i}`}
                          style={{ marginLeft: 8 }}
                        >
                          Present
                        </label>
                      </FormControl>
                    )}
                  />
                  <Controller
                    name={`${name}.${i}.endDate`}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        required
                        disabled={
                          watch(`${name}.${i}.present`) ||
                          !watch(`${name}.${i}.startDate`) ||
                          disabled ||
                          data._destroy
                        }
                        error={!!error}
                        sx={{ flex: 1, marginTop: "0px !important" }}
                      >
                        {/* <FormLabel>&nbsp;</FormLabel> */}
                        <DatePicker
                          selected={field?.value}
                          disabled={
                            watch(`${name}.${i}.present`) ||
                            !watch(`${name}.${i}.startDate`) ||
                            disabled ||
                            data._destroy
                          }
                          name="endDate"
                          minDate={watch(`${name}.${i}.startDate`)}
                          onSelect={(date: any) => {
                            if (date) {
                              const formattedDate =
                                dayjs(date).format("YYYY-MM-DD");
                              field.onChange(formattedDate);
                            }
                          }}
                        />
                      </FormControl>
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>

            <CareerDetailTabs
              errors={{
                // @ts-ignore
                action: Boolean(errors?.careers?.at(i)?.careerActions),
                // @ts-ignore
                position: Boolean(errors?.careers?.at(i)?.careerPositions),
              }}
              sx={{ width: "fit-content", my: 4 }}
              render={(currTabIndex) =>
                currTabIndex === 0 ? (
                  <CareerActionsForm
                    name={`${name}.${i}.careerActions`}
                    disabled={disabled || data._destroy}
                  />
                ) : (
                  <CareerPositionsForm
                    name={`${name}.${i}.careerPositions`}
                    disabled={disabled || data._destroy}
                  />
                )
              }
            />
          </AccordionDetails>
          <ConditionalRenderer condition={isEdit ? true : fields?.length > 1}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Button
                sx={{
                  backgroundColor: data._destroy ? "#00AAB0" : "white",
                  color: data._destroy ? "white" : "#FA6262",
                  borderColor: data?._destroy ? "#FA6262" : "#00AAB0",
                  border: "1px solid",
                  outline: "#00AAB0",
                  "&:hover": {
                    backgroundColor: data?._destroy ? "#00AAB0" : "",
                  },
                  "&.Mui-focused": {
                    backgroundColor: data?._destroy ? "#00AAB0" : "",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: data?._destroy ? "#00AAB0" : "",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: data?._destroy ? "#00AAB0" : "",
                    },
                  },
                  width: "20%",
                  marginBottom: "20px",
                }}
                startIcon={
                  data._destroy ? (
                    <MdCheck size={24} />
                  ) : data.id ? (
                    <MdDeleteForever size={24} />
                  ) : (
                    <MdOutlineRemove size={24} />
                  )
                }
                disabled={disabled}
                onClick={handleRemoveAction(i)}
                size="large"
              >
                {data._destroy
                  ? "Keep"
                  : data.id
                  ? translator("generic.DELETE")
                  : translator("generic.Remove")}
              </Button>
            </div>
          </ConditionalRenderer>
        </Accordion>
      ))}
    </>
  );
};

export default CareerForm;
